import { TheraApi } from '@thera-hr/api-sdk';

/**
 * SDK-sourced types. `WhiteLabelClient` lives under the `db` namespace after the refactor
 * unified it as a single Fern-generated class (matches how Company/Contract/User are structured).
 * `WhiteLabelClientBody` and `WhiteLabelClientCompanyView` stay under the API path because they're
 * a request body and response view — not DB entities.
 *
 * Durable identifiers are the `*S3Key` fields — those are what get persisted and re-submitted
 * on update. The `*Url` fields are freshly-signed (~1 hour validity) by the BE on every response.
 * Display only — never persist them.
 */
export type WhiteLabelClient = TheraApi.db.whiteLabel.WhiteLabelClient;
export type WhiteLabelClientBody = TheraApi.api.thera.whitelabel.whiteLabelApi.WhiteLabelClientBody;
export type WhiteLabelClientCompanyView = TheraApi.api.thera.whitelabel.whiteLabelApi.WhiteLabelClientCompanyView;

const jsonOrThrow = async <T>(response: Response, defaultMessage: string): Promise<T> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || defaultMessage);
  }
  return response.json();
};

/**
 * Public intake — prospective WLC submits their branding metadata. The BE pins status="draft"
 * server-side and rejects privileged fields, so prospects can't elevate. Admin CRUD on WLC
 * records lives in the Retool dashboard, which calls the BE's password-gated /dev path directly.
 *
 * Stays a Next.js proxy fetch (not an SDK call) because the intake form runs unauthenticated —
 * the Fern SDK client requires a Stytch JWT, which prospects don't have.
 */
export const createWhiteLabelClientPublic = async (body: WhiteLabelClientBody): Promise<WhiteLabelClient> => {
  const response = await fetch('/api/white-label-clients', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jsonOrThrow<WhiteLabelClient>(response, 'Failed to submit white label application');
};

/**
 * Public hostname → WLC lookup. Returns null if no WLC is configured for the given domain
 * (e.g. the user is on app.getthera.com or a preview domain). Same auth caveat as
 * [createWhiteLabelClientPublic] — stays a proxy fetch since the caller may be unauthenticated.
 */
export const getWhiteLabelClientByDomain = async (domain: string): Promise<WhiteLabelClient | null> => {
  const response = await fetch(`/api/white-label-clients/by-domain?domain=${encodeURIComponent(domain)}`, {
    method: 'GET',
  });
  if (response.status === 404) return null;
  return jsonOrThrow<WhiteLabelClient>(response, 'Failed to resolve white label client');
};
